import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    ModalCloseButton,
    Button,
    Text,
    FormControl,
    Input,
    Popover,
    PopoverTrigger,
    PopoverContent,
    PopoverArrow,
    PopoverBody,
    useDisclosure,
} from '@chakra-ui/react';
import React, { useState } from 'react';

interface ConfirmAddProviderModalProps {
    isOpen: boolean;
    supplierName: string | null;
    onChangeSupplierName: (name: string) => void;
    onConfirm: () => Promise<void> | void;
    onCancel: () => void;
    isLoading?: boolean;
}
export function ConfirmAddProviderModal({
    isOpen,
    supplierName,
    onChangeSupplierName,
    onConfirm,
    onCancel,
    isLoading = false,
}: ConfirmAddProviderModalProps) {
    const [error, setError] = useState<string | null>(null);
    const errorDisclosure = useDisclosure();

    const handleConfirm = async () => {
        try {
            setError(null);
            await onConfirm();
        } catch (err) {
            const errMessage = typeof err === 'object' && err !== null && 'message' in err
                ? String((err).message)
                : null;

            if (errMessage !== null && errMessage.trim() !== '') {
                setError(`Failed to add service provider: ${errMessage}`);
            } else {
                setError('Failed to add service provider. Please contact IT team.');
            }
            errorDisclosure.onOpen();
        }
    };

    React.useEffect(() => {
        if (!isOpen) {
            setError(null);
            errorDisclosure.onClose();
        }
    }, [isOpen]);

    return (
        <Modal isOpen={isOpen} onClose={onCancel} size="md" isCentered>
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>Add new service provider</ModalHeader>
                <ModalCloseButton />

                <ModalBody>
                    <Text mb={3}>
                        Please type the full name of the service provider and click Add
                    </Text>
                    <FormControl>
                        <Input
                            value={supplierName ?? ''}
                            onChange={(e) => {
                                onChangeSupplierName(e.target.value);
                                if (error !== null) setError(null); // reset error when typing
                            }}
                            placeholder="Enter service provider name"
                            autoFocus
                        />
                    </FormControl>
                </ModalBody>

                <ModalFooter>
                    <Button
                        variant="ghost"
                        colorScheme="muted"
                        mr={3}
                        onClick={onCancel}
                    >
                        Cancel
                    </Button>

                    {/* Popover wraps Add button */}
                    <Popover
                        isOpen={error !== null && errorDisclosure.isOpen}
                        placement="top"
                        isLazy
                    >
                        <PopoverTrigger>
                            <Button
                                onClick={handleConfirm}
                                isLoading={isLoading}
                                colorScheme={error === null ? 'blue' : 'red'}
                                onMouseEnter={() => {
                                    if (error !== null) errorDisclosure.onOpen();
                                }}
                                onMouseLeave={() => errorDisclosure.onClose()}
                            >
                                Add
                            </Button>
                        </PopoverTrigger>

                        <PopoverContent
                            w="max-content"
                            pointerEvents="none"
                            bg="red.50"
                            borderColor="red.200"
                        >
                            <PopoverArrow bg="red.50" />
                            <PopoverBody fontSize="sm">
                                <Text fontWeight="bold" color="red.600">
                                    Add failed
                                </Text>
                                <Text mb={2} color="red.600">
                                    {error ?? 'An error occurred while adding. Please try again.'}
                                </Text>
                            </PopoverBody>
                        </PopoverContent>
                    </Popover>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}
